<template>
  <div>
    <TwcButton
      v-if="stripeStore.isApplePayAvailable"
      data-testid="button-apple-pay"
      class="w-full mb-3 h-12 button-apple-pay-with-text button-apple-pay-black-with-text hover:bg-black"
      @click="makePayment(PAYMENT_METHOD.APPLE_WALLET)"
    >
      Apple Pay
    </TwcButton>
    <TwcButton
      v-if="stripeStore.isGooglePayAvailable"
      data-testid="button-gpay"
      class="w-full mb-3 h-12 button-gpay hover:bg-black"
      @click="makePayment(PAYMENT_METHOD.GOOGLE_WALLET)"
    />
    <TwcButton
      v-if="stripeStore.setupApplePay"
      data-testid="button-setup-apple-pay"
      class="w-full mb-3 h-12 button-apple-pay-with-text button-apple-pay-black-with-text hover:bg-black button-apple-pay-set-up"
      :disabled="isSettingUpApplePay"
      @click="setupApplePayClicked"
    />
    <div v-if="errorMessage" class="text-sm text-red-600 dark:text-red-500 mt-1">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { TwcButton } from 'transit-web-components'
import { useStripeStore } from '@/stores/stripe'
import { useAuthStore } from '@/stores/auth'
import { useCustomerStore } from '@/stores/customer'
import { PAYMENT_METHOD, useOrderStore } from '@/stores/order'
import { createOrder } from '@/services/api/orders'
import { requestPaymentIntent } from '@/services/api/payments'
import { TICKETS_ROUTES } from '@/router'
import Honeybadger from '@honeybadger-io/js'
import { getError } from '@/utils/errors'
import { useGoogleTagManagerStore } from '@/stores/google-tag-manager'

const stripeStore = useStripeStore()
const orderStore = useOrderStore()
const errorMessage = ref()
const router = useRouter()
const isSettingUpApplePay = ref(false)
const authStore = useAuthStore()
const { isAuthenticated } = storeToRefs(authStore)
const customerStore = useCustomerStore()

async function makePayment(wallet: PAYMENT_METHOD) {
  errorMessage.value = null

  const onPaymentMethodCompleted = async (event: any) => {
    let customerEmail = event.payerEmail || event.paymentMethod.billing_details?.email

    if (isAuthenticated.value) {
      await customerStore.loadFromSessionToken()
      customerEmail = customerStore.email || customerEmail
    }

    orderStore.customerEmail = customerEmail
    orderStore.customerName = event.payerName || event.paymentMethod.billing_details?.name
    orderStore.customerPhone = event.payerPhone || event.paymentMethod.billing_details?.phone
    orderStore.customerAddressLine1 = event.paymentMethod.billing_details?.address?.line1
    orderStore.customerAddressLine2 = event.paymentMethod.billing_details?.address?.line2
    orderStore.customerCity = event.paymentMethod.billing_details?.address?.city
    orderStore.customerCountry = event.paymentMethod.billing_details?.address?.country
    orderStore.customerPostalCode = event.paymentMethod.billing_details?.address?.postal_code
    orderStore.customerState = event.paymentMethod.billing_details?.address?.state
    orderStore.paymentMethod = wallet
    orderStore.customerWalletEmail = event.payerEmail || event.paymentMethod.billing_details?.email

    const resultIntent = await requestPaymentIntent(event.paymentMethod.id)

    if (resultIntent.error) {
      event.complete('fail')
      errorMessage.value = getError(resultIntent)
    } else {
      event.complete('success')
      orderStore.isProcessing = true
      const resultOrder = await createOrder(resultIntent.paymentIntentId)

      if (!resultOrder.error) {
        orderStore.updateFromResponse(resultOrder)
        useGoogleTagManagerStore().trackOrderCompleted(orderStore.id, orderStore.total)
        router.push({ name: TICKETS_ROUTES.ORDER_CONFIRMATION, params: { id: orderStore.id } })
      } else {
        errorMessage.value = getError(resultOrder)
      }
      orderStore.isProcessing = false
    }
  }
  await stripeStore.requestWalletPaymentMethod(
    orderStore.totalInCents,
    onPaymentMethodCompleted,
    !isAuthenticated.value || wallet == PAYMENT_METHOD.GOOGLE_WALLET
  )
}

function setupApplePayClicked() {
  isSettingUpApplePay.value = true
  errorMessage.value = null

  const onError = (e: any) => {
    errorMessage.value = 'Cannot setup Apple Pay. Your territory may not be supported.'
    stripeStore.setupApplePay = false
    Honeybadger.notify('Cannot setup Apple Pay', { context: { error: e }, tags: 'info, apple' })
  }

  const merchantIdentifier = stripeStore.appleMerchantIdentifier
  window.ApplePaySession.openPaymentSetup(merchantIdentifier)
    .then((success: any) => {
      if (!success) onError('')
    })
    .catch(onError)
    .finally(() => {
      isSettingUpApplePay.value = false
    })
}
</script>

<style>
.button-gpay {
  background-color: black;
  box-shadow: none;
  padding: 12px 24px 10px;
  background-repeat: no-repeat;
  background-origin: content-box;
  background-position: center center;
  background-size: contain;
  background-image: url('@/assets/images/gpay.svg');
}

/* Documentation: https://developer.apple.com/documentation/apple_pay_on_the_web/displaying_apple_pay_buttons */
@supports (-webkit-appearance: -apple-pay-button) {
  .button-apple-pay-with-text {
    display: inline-block;
    -webkit-appearance: -apple-pay-button;
    -apple-pay-button-type: buy;
  }

  .button-apple-pay-with-text.button-apple-pay-set-up {
    -apple-pay-button-type: set-up;
  }

  .button-apple-pay-with-text > * {
    display: none;
  }

  .button-apple-pay-black-with-text {
    -apple-pay-button-style: black;
  }

  .button-apple-pay-white-with-text {
    -apple-pay-button-style: white;
  }

  .button-apple-pay-white-with-line-with-text {
    -apple-pay-button-style: white-outline;
  }
}

@supports not (-webkit-appearance: -apple-pay-button) {
  .button-apple-pay-with-text {
    --apple-pay-scale: 1; /* (height / 32) */
    display: inline-flex;
    justify-content: center;
    font-size: 12px;
    border-radius: 5px;
    padding: 0px;
    box-sizing: border-box;
    min-width: 200px;
    min-height: 32px;
    max-height: 64px;
  }
  .button-apple-pay-black-with-text {
    background-color: black;
    color: white;
  }
  .button-apple-pay-white-with-text {
    background-color: white;
    color: black;
  }
  .button-apple-pay-white-with-line-with-text {
    background-color: white;
    color: black;
    border: 0.5px solid black;
  }
  .button-apple-pay-with-text.button-apple-pay-black-with-text > .logo {
    background-image: -webkit-named-image(apple-pay-logo-white);
    background-color: black;
  }
  .button-apple-pay-with-text.button-apple-pay-white-with-text > .logo {
    background-image: -webkit-named-image(apple-pay-logo-black);
    background-color: white;
  }
  .button-apple-pay-with-text.button-apple-pay-white-with-line-with-text > .logo {
    background-image: -webkit-named-image(apple-pay-logo-black);
    background-color: white;
  }
  .button-apple-pay-with-text > .text {
    font-family: -apple-system;
    font-size: calc(1em * var(--apple-pay-scale));
    font-weight: 300;
    align-self: center;
    margin-right: calc(2px * var(--apple-pay-scale));
  }
  .button-apple-pay-with-text > .logo {
    width: calc(35px * var(--scale));
    height: 100%;
    background-size: 100% 60%;
    background-repeat: no-repeat;
    background-position: 0 50%;
    margin-left: calc(2px * var(--apple-pay-scale));
    border: none;
  }
}
</style>
